import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Lightbox({ images, initialIndex, isOpen, onClose }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    startIndex: initialIndex || 0,
  });
  const [selected, setSelected] = useState(initialIndex || 0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Sync embla position when opening
  useEffect(() => {
    if (emblaApi && isOpen) {
      emblaApi.scrollTo(initialIndex, true);
      setSelected(initialIndex);
    }
  }, [initialIndex, isOpen, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") scrollPrev();
      else if (e.key === "ArrowRight") scrollNext();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose, scrollPrev, scrollNext]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Top bar: counter + close */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 md:px-10 py-5 z-10 pointer-events-none">
        <span
          data-testid="lightbox-counter"
          className="text-white text-[10px] uppercase tracking-[0.3em] pointer-events-auto"
        >
          {String(selected + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          data-testid="lightbox-close"
          className="pointer-events-auto w-11 h-11 flex items-center justify-center text-white/85 hover:text-white transition-colors"
        >
          <X size={22} strokeWidth={1.5} />
        </button>
      </div>

      {/* Carousel */}
      <div className="overflow-hidden w-full h-full" ref={emblaRef}>
        <div className="flex h-full">
          {images.map((img, i) => (
            <div
              key={i}
              className="flex-[0_0_100%] min-w-0 h-full flex items-center justify-center px-4 md:px-20 py-16 md:py-20"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="max-h-[85vh] max-w-full object-contain select-none"
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Foto precedente"
            data-testid="lightbox-prev"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={28} strokeWidth={1.25} />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Foto successiva"
            data-testid="lightbox-next"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={28} strokeWidth={1.25} />
          </button>
        </>
      )}
    </div>
  );
}
